import React, { useState, useEffect, useMemo } from 'react';
import { Modal, message, Button, Spin } from 'antd';
import {
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  WarningOutlined,
  DeleteOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useIntl } from 'react-intl';
import api from '../../../utils/service/api';
import { actionDeleteStudies } from '../StudyListAction';
import { isEmpty } from '../../../utils/helpers';

/* ── AfyaNX brand colors ─────────────────────────────────────────── */
const C = {
  teal: '#2a5f5a',
  tealSoft: 'rgba(42, 95, 90, 0.08)',
  tealBorder: 'rgba(42, 95, 90, 0.25)',
  amber: '#d97706',
  amberBg: 'rgba(217, 119, 6, 0.08)',
  amberBorder: 'rgba(217, 119, 6, 0.35)',
  red: '#dc2626',
  redBg: 'rgba(220, 38, 38, 0.08)',
  redBorder: 'rgba(220, 38, 38, 0.35)',
  gray900: '#111827',
  gray700: '#374151',
  gray600: '#4b5563',
  gray500: '#6b7280',
};

const STATUS = {
  UNASSIGNED: 'UNASSIGNED',
  ASSIGNED: 'ASSIGNED',
  COMPLETED: 'COMPLETED',
};

const displayNameFromUser = (u) => {
  if (!u) return '';
  const first = (u.firstName || u.first_name || '').trim();
  const last = (u.lastName || u.last_name || '').trim();
  const full = `${first} ${last}`.trim();
  return full || u.username || u.email || u.id || '';
};

const DeleteStudyModal = (props) => {
  const intl = useIntl();
  const { formatMessage: t } = intl;
  const { visible = true, onCancel, selectedRowKeys, selectedRowItems = [] } = props;

  const [processing, setProcessing] = useState(false);
  const [deletedInfo, setDeletedInfo] = useState();
  const [loadingAssignees, setLoadingAssignees] = useState(false);
  const [studyAssignees, setStudyAssignees] = useState({});

  const { unassignedItems, assignedItems, completedItems } = useMemo(() => {
    const unassigned = [];
    const assigned = [];
    const completed = [];
    (selectedRowItems || []).forEach((it) => {
      const s = (it?.status || '').toUpperCase();
      if (s === STATUS.ASSIGNED) assigned.push(it);
      else if (s === STATUS.COMPLETED) completed.push(it);
      else unassigned.push(it);
    });
    return { unassignedItems: unassigned, assignedItems: assigned, completedItems: completed };
  }, [selectedRowItems]);

  const hasProtectedStudies = assignedItems.length > 0 || completedItems.length > 0;

  useEffect(() => {
    if (!visible || !hasProtectedStudies || !isEmpty(deletedInfo)) return;

    let cancelled = false;
    const protectedItems = [...assignedItems, ...completedItems];

    (async () => {
      setLoadingAssignees(true);
      try {
        const taskResults = await Promise.all(
          protectedItems.map((item) =>
            api({
              method: 'get',
              url: '/api/tasks',
              params: { study_id: item.id, _offset: 0, _limit: 10 },
            })
              .then((r) => ({ studyId: item.id, tasks: r?.data?.data || [] }))
              .catch(() => ({ studyId: item.id, tasks: [] }))
          )
        );

        const assigneeIds = new Set();
        taskResults.forEach(({ tasks }) => {
          tasks.forEach((task) => {
            if (task?.assignee_id && !task?.archived) assigneeIds.add(task.assignee_id);
          });
        });

        let idToName = {};
        if (assigneeIds.size > 0) {
          try {
            const { data } = await api({ method: 'get', url: '/api/accounts/userinfo' });
            const users = data?.data || [];
            users.forEach((u) => {
              if (u?.id) idToName[u.id] = displayNameFromUser(u);
            });
          } catch (_) {
            /* fall back to raw ids */
          }
        }

        const map = {};
        taskResults.forEach(({ studyId, tasks }) => {
          const seen = new Set();
          const list = [];
          tasks.forEach((task) => {
            const aid = task?.assignee_id;
            if (!aid || task?.archived || seen.has(aid)) return;
            seen.add(aid);
            list.push({ id: aid, name: idToName[aid] || aid });
          });
          if (list.length > 0) map[studyId] = list;
        });

        if (!cancelled) setStudyAssignees(map);
      } catch (_) {
        /* non-fatal */
      } finally {
        if (!cancelled) setLoadingAssignees(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const handleDelete = async () => {
    if (isEmpty(selectedRowKeys) || processing) return;
    try {
      setProcessing(true);
      const { data } = await actionDeleteStudies(selectedRowKeys);
      setDeletedInfo(data?.meta);
      setProcessing(false);
    } catch (error) {
      message.error('Delete request failed');
      setProcessing(false);
    }
  };

  const handleClose = () => {
    if (onCancel) onCancel(!isEmpty(deletedInfo));
  };

  const footerAction = () => {
    if (!isEmpty(deletedInfo)) {
      return [
        <Button key="close" onClick={handleClose}>
          {t({ id: 'IDS_CLOSE' })}
        </Button>,
      ];
    }
    return [
      <Button key="cancel" onClick={handleClose} disabled={processing}>
        {t({ id: 'IDS_CANCEL' }) || 'Cancel'}
      </Button>,
      <Button
        key="submit"
        type="primary"
        danger
        icon={<DeleteOutlined />}
        onClick={handleDelete}
        loading={processing}
        className="btn-delete"
      >
        {hasProtectedStudies ? 'Force Delete' : t({ id: 'IDS_DELETE' })}
      </Button>,
    ];
  };

  const SeverityCard = ({ tone, icon, title, children }) => {
    const tones = {
      teal: { bg: C.tealSoft, border: C.tealBorder, accent: C.teal },
      amber: { bg: C.amberBg, border: C.amberBorder, accent: C.amber },
      red: { bg: C.redBg, border: C.redBorder, accent: C.red },
    };
    const tn = tones[tone] || tones.teal;
    return (
      <div
        style={{
          background: tn.bg,
          border: `1px solid ${tn.border}`,
          borderRadius: 8,
          padding: 12,
          marginBottom: 10,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 10,
        }}
      >
        <span style={{ color: tn.accent, fontSize: 18, flexShrink: 0, marginTop: 1 }}>{icon}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          {title && (
            <div
              style={{
                color: tn.accent,
                fontWeight: 600,
                fontSize: 13,
                marginBottom: children ? 4 : 0,
              }}
            >
              {title}
            </div>
          )}
          {children && (
            <div style={{ color: C.gray700, fontSize: 13, lineHeight: 1.45 }}>{children}</div>
          )}
        </div>
      </div>
    );
  };

  const renderProtectedList = (items, tone) =>
    items.map((item) => {
      const list = studyAssignees[item.id] || [];
      const names = list.map((a) => a.name).join(', ');
      return (
        <div key={item.id} style={{ marginTop: 6, fontSize: 13 }}>
          <span style={{ fontWeight: 600, color: C.gray900 }}>{item.code || item.id}</span>
          {list.length > 0 ? (
            <span style={{ color: C.gray600 }}>
              {' — '}
              <UserOutlined
                style={{ marginRight: 4, color: tone === 'red' ? C.red : C.amber }}
              />
              {names}
            </span>
          ) : (
            loadingAssignees && (
              <span style={{ color: C.gray500, marginLeft: 6, fontSize: 12 }}>
                (loading…)
              </span>
            )
          )}
        </div>
      );
    });

  return (
    <Modal
      visible={visible}
      className="common-modal delete-modal"
      onCancel={handleClose}
      width={520}
      maskClosable={false}
      footer={footerAction()}
      title={
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, color: C.gray900 }}>
          <ExclamationCircleOutlined style={{ color: C.red, fontSize: 18 }} />
          <span style={{ fontWeight: 600 }}>Delete studies</span>
        </span>
      }
    >
      <Spin spinning={processing}>
        <div className="delete-modal-content" style={{ paddingTop: 4 }}>
          {isEmpty(deletedInfo) && (
            <>
              <div
                style={{
                  color: C.gray700,
                  fontSize: 14,
                  marginBottom: 12,
                  lineHeight: 1.5,
                }}
              >
                You are about to delete{' '}
                <strong style={{ color: C.gray900 }}>
                  {selectedRowKeys?.length || 0} stud
                  {selectedRowKeys?.length === 1 ? 'y' : 'ies'}
                </strong>{' '}
                from this project. This action cannot be undone.
              </div>

              {unassignedItems.length > 0 && (
                <SeverityCard
                  tone="teal"
                  icon={<CheckCircleOutlined />}
                  title={`${unassignedItems.length} unassigned ${
                    unassignedItems.length === 1 ? 'study' : 'studies'
                  } — safe to delete`}
                >
                  DICOM images and project record will be permanently removed.
                </SeverityCard>
              )}

              {assignedItems.length > 0 && (
                <SeverityCard
                  tone="amber"
                  icon={<WarningOutlined />}
                  title={`${assignedItems.length} assigned ${
                    assignedItems.length === 1 ? 'study is' : 'studies are'
                  } currently in a radiologist's worklist`}
                >
                  <div>
                    Deleting will remove the assignment and the in-progress annotation task
                    from the worklist. Consider revoking the assignment first.
                  </div>
                  {renderProtectedList(assignedItems, 'amber')}
                </SeverityCard>
              )}

              {completedItems.length > 0 && (
                <SeverityCard
                  tone="red"
                  icon={<WarningOutlined />}
                  title={`${completedItems.length} completed ${
                    completedItems.length === 1 ? 'study has' : 'studies have'
                  } finalized annotations`}
                >
                  <div>
                    Deleting will permanently destroy all labels, findings, and annotation
                    objects associated with{' '}
                    {completedItems.length === 1 ? 'this study' : 'these studies'}.
                  </div>
                  {renderProtectedList(completedItems, 'red')}
                </SeverityCard>
              )}
            </>
          )}

          {!isEmpty(deletedInfo) && (
            <>
              {deletedInfo.deleted > 0 && (
                <SeverityCard
                  tone="teal"
                  icon={<CheckCircleOutlined />}
                  title={`${deletedInfo.deleted} stud${
                    deletedInfo.deleted === 1 ? 'y' : 'ies'
                  } deleted successfully`}
                />
              )}
              {deletedInfo?.not_deleted > 0 && (
                <SeverityCard
                  tone="red"
                  icon={<CloseCircleOutlined />}
                  title={`${deletedInfo.not_deleted} stud${
                    deletedInfo.not_deleted === 1 ? 'y' : 'ies'
                  } could not be deleted`}
                >
                  The record was not found or had already been removed.
                </SeverityCard>
              )}
            </>
          )}
        </div>
      </Spin>
    </Modal>
  );
};

export default DeleteStudyModal;
